/**
 * AST-driven cursor context for CEL suggestion dispatch.
 *
 * Replaces source-text regex scans (`/cel\.bind\s*\(\s*\w+\s*,/`,
 * `/\.(exists|all|...)\s*\(\s*\w+\s*,/`) with structural walking of the
 * @bufbuild/cel ParsedExpr tree, mirroring what the Java engine does via
 * locateCursor + enclosingComprehension.
 *
 * The pipeline:
 *   1. injectCursor: place CURSOR sentinel at the cursor offset and balance
 *      unclosed parens/brackets so the result parses.
 *   2. parse: @bufbuild/cel.parse returns an Expr tree.
 *   3. locateCursor: recursive descent that finds the sentinel-bearing node
 *      and returns the ancestor path.
 *   4. comprehensionChain: ancestor Comprehensions from innermost to
 *      outermost — gives lambda iter-vars and cel.bind binding names + sources.
 */

import {parse} from '@bufbuild/cel'
import type {Expr, Expr_Comprehension} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'
import {CURSOR} from './CelSuggestionReplacements'

export type CursorContext = {
    /** The sentinel-bearing Expr node (cursor sits inside this node's text). */
    readonly cursorNode: Expr
    /** The immediate parent of cursorNode, or null when the cursor is the root. */
    readonly parent: Expr | null
    /** All ancestors of cursorNode, root-first (parent at end). */
    readonly path: readonly Expr[]
    /** Enclosing Comprehension Exprs, innermost first. */
    readonly comprehensionChain: readonly Expr[]
    /** Enclosing cel.bind Calls (the parser keeps them as Calls), innermost first. */
    readonly celBindChain: readonly CelBindFrame[]
}

/** A cel.bind call ancestor: the call node + which argument position contains the cursor. */
export type CelBindFrame = {
    readonly call: Expr
    /** Arg index containing the cursor: 0=binding name, 1=source, 2=body. */
    readonly argIndex: number
    /** Binding name (args[0]'s ident name) when statically resolvable, else null. */
    readonly bindingName: string | null
    /** Source expression (args[1]) when present. */
    readonly source: Expr | null
}

/**
 * Inject the CURSOR sentinel at the cursor offset, balancing any unclosed
 * `(`, `[`, or open string literals so the result parses. Returns the
 * source string the parser will consume.
 */
export function injectCursor(source: string, cursor: number): string {
    const before = source.substring(0, cursor)
    const after = source.substring(cursor)
    // NOTE: 1. Open-string handling: count unescaped quotes in `before`.
    //    If a quote is open, close it before the sentinel so the parser sees
    //    a complete string. The sentinel goes AFTER the closing quote as a
    //    follow-up token, which the AST walker can find as a separate node.
    const openQuote = unterminatedQuote(before)
    let injectedBefore = before
    let injectedAfter = after
    if (openQuote !== '') {
        // Find next unescaped quote in `after` and split — anything past it is tail.
        const closeIdx = nextUnescapedQuote(after, openQuote)
        if (closeIdx >= 0) {
            // The string was actually closed past the cursor; treat as inside.
            injectedAfter = after.substring(0, closeIdx + 1) + ' ' + CURSOR + after.substring(closeIdx + 1)
            return injectedBefore + injectedAfter + balanceClosers(injectedBefore + injectedAfter)
        }
        // Truly unterminated — close it, drop sentinel as a follow-up ident.
        injectedBefore = before + openQuote
        injectedAfter = after
        return injectedBefore + ' ' + CURSOR + balanceClosers(injectedBefore + injectedAfter)
    }
    const composed = injectedBefore + CURSOR + injectedAfter
    return composed + balanceClosers(composed)
}

/**
 * Locate the Expr node whose source text contains the CURSOR sentinel.
 * Returns the ancestor path (root first) or null when no node carries the
 * cursor (parse produced an AST that doesn't include the sentinel anywhere).
 */
export function locateCursor(expr: Expr | undefined): readonly Expr[] | null {
    if (expr === undefined) return null
    return walkLocate(expr, [])
}

/**
 * Build the full CursorContext for a (source, cursor) pair. Returns null
 * when parsing fails or the cursor sentinel is not found in the AST.
 */
export function buildCursorContext(source: string, cursor: number): CursorContext | null {
    const injected = injectCursor(source, cursor)
    let exprTree: Expr | undefined
    try {
        const parsed = parse(injected)
        exprTree = parsed.expr
    } catch {
        return null
    }
    const path = locateCursor(exprTree)
    if (path === null || path.length === 0) return null
    const cursorNode = path[path.length - 1]
    const parent = path.length >= 2 ? path[path.length - 2] : null
    const comprehensionChain: Expr[] = []
    const celBindChain: CelBindFrame[] = []
    for (let i = path.length - 2; i >= 0; i--) {
        const node = path[i]
        if (node.exprKind.case === 'comprehensionExpr') comprehensionChain.push(node)
        if (isCelBindCall(node)) {
            const call = node.exprKind.case === 'callExpr' ? node.exprKind.value : null
            if (call !== null) {
                const childInPath = path[i + 1]
                const argIndex = call.args.findIndex(a => a === childInPath)
                const bindingName = call.args.length > 0 && call.args[0].exprKind.case === 'identExpr'
                    ? call.args[0].exprKind.value.name
                    : null
                const source = call.args.length > 1 ? call.args[1] : null
                celBindChain.push({call: node, argIndex, bindingName, source})
            }
        }
    }
    return {cursorNode, parent, path, comprehensionChain, celBindChain}
}

/**
 * `cel.bind(name, source, body)` parses as `Call(target=Ident('cel'),
 * function='bind', args=[name, source, body])` — the parser does NOT
 * desugar the macro. Detect it structurally.
 */
export function isCelBindCall(expr: Expr): boolean {
    if (expr.exprKind.case !== 'callExpr') return false
    const c = expr.exprKind.value
    if (c.function !== 'bind') return false
    const t = c.target
    if (t === undefined) return false
    return t.exprKind.case === 'identExpr' && t.exprKind.value.name === 'cel'
}

/** Returns the Ident name when the expr is a plain identifier, else null. */
export function identName(expr: Expr | undefined): string | null {
    if (expr === undefined) return null
    return expr.exprKind.case === 'identExpr' ? expr.exprKind.value.name : null
}

/**
 * Walk a Select / Ident chain and return the dotted path string, stopping
 * at the first non-path node. `pilot.ships.exists` → "pilot.ships.exists".
 * Anything not a pure path returns null.
 */
export function dottedPathOf(expr: Expr | undefined): string | null {
    if (expr === undefined) return null
    if (expr.exprKind.case === 'identExpr') return expr.exprKind.value.name
    if (expr.exprKind.case === 'selectExpr') {
        const parent = dottedPathOf(expr.exprKind.value.operand)
        if (parent === null) return null
        return `${parent}.${expr.exprKind.value.field}`
    }
    return null
}

export function comprehensionOf(expr: Expr): Expr_Comprehension | null {
    return expr.exprKind.case === 'comprehensionExpr' ? expr.exprKind.value : null
}

/**
 * cel.bind is parsed as a Comprehension where iterVar === '#unused'. Returns
 * the binding name (accuVar) when the comprehension is a cel.bind macro,
 * else null.
 */
export function celBindBindingName(expr: Expr): string | null {
    const c = comprehensionOf(expr)
    if (c === null) return null
    if (c.iterVar !== '#unused') return null
    return c.accuVar
}

/**
 * Returns the iter-var name when the comprehension is a list-macro (exists,
 * all, filter, map, exists_one), else null.
 */
export function listMacroIterVar(expr: Expr): string | null {
    const c = comprehensionOf(expr)
    if (c === null) return null
    if (c.iterVar === '#unused') return null
    return c.iterVar
}

// === internals ===

function walkLocate(expr: Expr, trail: Expr[]): readonly Expr[] | null {
    trail.push(expr)
    if (nodeContainsCursor(expr)) return [...trail]
    switch (expr.exprKind.case) {
        case 'selectExpr': {
            const operand = expr.exprKind.value.operand
            if (operand !== undefined) {
                const found = walkLocate(operand, trail)
                if (found !== null) return found
            }
            break
        }
        case 'callExpr': {
            const call = expr.exprKind.value
            if (call.target !== undefined) {
                const found = walkLocate(call.target, trail)
                if (found !== null) return found
            }
            for (const arg of call.args) {
                const found = walkLocate(arg, trail)
                if (found !== null) return found
            }
            break
        }
        case 'listExpr': {
            for (const elem of expr.exprKind.value.elements) {
                const found = walkLocate(elem, trail)
                if (found !== null) return found
            }
            break
        }
        case 'structExpr': {
            for (const entry of expr.exprKind.value.entries) {
                if (entry.keyKind.case === 'mapKey') {
                    const f = walkLocate(entry.keyKind.value, trail)
                    if (f !== null) return f
                }
                if (entry.value !== undefined) {
                    const f = walkLocate(entry.value, trail)
                    if (f !== null) return f
                }
            }
            break
        }
        case 'comprehensionExpr': {
            const c = expr.exprKind.value
            for (const child of [c.iterRange, c.accuInit, c.loopCondition, c.loopStep, c.result]) {
                if (child === undefined) continue
                const f = walkLocate(child, trail)
                if (f !== null) return f
            }
            break
        }
        default:
            break
    }
    trail.pop()
    return null
}

function nodeContainsCursor(expr: Expr): boolean {
    switch (expr.exprKind.case) {
        case 'identExpr':
            return expr.exprKind.value.name.includes(CURSOR)
        case 'selectExpr':
            return expr.exprKind.value.field.includes(CURSOR)
        case 'constExpr': {
            const k = expr.exprKind.value.constantKind
            if (k.case === 'stringValue') return typeof k.value === 'string' && k.value.includes(CURSOR)
            return false
        }
        default:
            return false
    }
}

function unterminatedQuote(s: string): string {
    let open = ''
    for (let i = 0; i < s.length; i++) {
        const c = s.charAt(i)
        if (open !== '' && c === '\\' && i + 1 < s.length) {
            i++
            continue
        }
        if (open === '') {
            if (c === "'" || c === '"') open = c
        } else if (c === open) {
            open = ''
        }
    }
    return open
}

function nextUnescapedQuote(s: string, quote: string): number {
    for (let i = 0; i < s.length; i++) {
        const c = s.charAt(i)
        if (c === '\\' && i + 1 < s.length) {
            i++
            continue
        }
        if (c === quote) return i
    }
    return -1
}

function balanceClosers(source: string): string {
    let parens = 0
    let brackets = 0
    let inSingle = false
    let inDouble = false
    for (let i = 0; i < source.length; i++) {
        const c = source.charAt(i)
        if (inSingle || inDouble) {
            if (c === '\\' && i + 1 < source.length) {
                i++
                continue
            }
            if (inSingle && c === "'") inSingle = false
            else if (inDouble && c === '"') inDouble = false
            continue
        }
        if (c === "'") inSingle = true
        else if (c === '"') inDouble = true
        else if (c === '(') parens++
        else if (c === ')') parens--
        else if (c === '[') brackets++
        else if (c === ']') brackets--
    }
    let tail = ''
    for (let i = 0; i < brackets; i++) tail += ']'
    for (let i = 0; i < parens; i++) tail += ')'
    return tail
}
